import React, { useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useT } from "@/i18n";

// Uploads a file and returns an absolute, publicly-readable URL.
export default function ImageUpload({ value, onChange, multiple = false, label, testid = "image-upload" }) {
  const t = useT();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
        const absolute = `${API.replace(/\/api$/, "")}${data.url}`;
        uploaded.push(absolute);
      }
      if (multiple) onChange([...(Array.isArray(value) ? value : []), ...uploaded]);
      else onChange(uploaded[0]);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (url) => {
    if (multiple) onChange(images.filter((u) => u !== url));
    else onChange("");
  };

  return (
    <div>
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <div className="flex flex-wrap gap-3">
        {images.map((url) => (
          <div key={url} className="relative h-20 w-20 overflow-hidden rounded-md border border-border">
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => remove(url)} data-testid={`${testid}-remove`}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white transition-colors hover:bg-black">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {(multiple || images.length === 0) && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testid}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-60">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            <span className="text-[10px]">{uploading ? t("store.uploading") : t("products.addImage")}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
        onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} />
    </div>
  );
}
